/**
 * W-021 — Content item form (create + edit). Stories: US-A03, US-A04, US-A05.
 *
 * AC1 type selector: lesson · short lesson · exercise
 * AC2 bilingual title and description, video, optional PDF, order, free preview
 * AC3 publishing is blocked while the video is not ready
 * AC6 duplicate lives on the list, since it acts on a saved record
 */
import { Create, Edit, useForm } from '@refinedev/antd'
import type { HttpError } from '@refinedev/core'
import { Alert, Card, Form, InputNumber, Select, Switch, Typography } from 'antd'
import type { FormInstance } from 'antd'
import { useState } from 'react'
import { BilingualField } from '@/components'
import { recordAudit } from '@/lib/audit'
import { useAdminTranslate } from '@/providers/i18n-provider'
import { checkBilingualForPublish } from '@/lib/bilingual'
import type { ContentItem, ContentType } from '@/types'
import { CONTENT_TYPES } from '@/types'
import { ContentAnalyticsPanel } from './ContentAnalyticsPanel'
import { PdfUploadPanel } from './PdfUploadPanel'
import { useSubjectOptions } from './useSubjectOptions'
import { VideoUploadPanel } from './VideoUploadPanel'

const Fields = ({
  form,
  record,
  publishBlocked,
  onPublishToggle,
}: {
  form: FormInstance<ContentItem>
  record?: ContentItem
  publishBlocked: string | null
  onPublishToggle: (next: boolean) => void
}) => {
  const translate = useAdminTranslate()
  const { subjects, isLoading } = useSubjectOptions()
  const [pdfPath, setPdfPath] = useState<string | null>(record?.pdf_path ?? null)

  return (
    <>
      <Card
        size="small"
        title={translate('admin.screens.contentForm.attachment')}
        style={{ marginBottom: 16 }}
      >
        <Form.Item
          name="subject_id"
          label={translate('admin.columns.subject')}
          rules={[
            { required: true, message: translate('admin.screens.contentForm.subjectRequired') },
          ]}
        >
          <Select
            loading={isLoading}
            showSearch
            optionFilterProp="label"
            options={subjects.map(subject => ({
              value: subject.id,
              label: `${subject.name_fr} — ${subject.name_ar}`,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="type"
          label={translate('admin.common.type')}
          rules={[{ required: true, message: translate('admin.screens.contentForm.typeRequired') }]}
          initialValue={'lesson' satisfies ContentType}
        >
          <Select
            options={CONTENT_TYPES.map(value => ({
              value,
              label: translate(`admin.contentTypes.${value}`),
            }))}
          />
        </Form.Item>

        <Form.Item name="sort_order" label={translate('admin.common.order')} initialValue={0}>
          <InputNumber min={0} />
        </Form.Item>
      </Card>

      <Card
        size="small"
        title={translate('admin.screens.contentForm.contentSection')}
        style={{ marginBottom: 16 }}
      >
        <BilingualField
          name="title"
          label={translate('admin.columns.title')}
          requiredAr
          maxLength={160}
        />
        <BilingualField
          name="description"
          label={translate('admin.common.description')}
          multiline
          maxLength={2000}
        />
      </Card>

      {record ? (
        <>
          <Card
            size="small"
            title={translate('admin.screens.contentForm.videoSection')}
            style={{ marginBottom: 16 }}
          >
            <VideoUploadPanel
              contentId={record.id}
              title={record.title_fr || record.title_ar}
              videoId={record.video_id}
              videoStatus={record.video_status}
              videoError={record.video_error}
              durationSec={record.duration_sec}
            />
          </Card>

          <Card
            size="small"
            title={translate('admin.screens.contentForm.documentSection')}
            style={{ marginBottom: 16 }}
          >
            <PdfUploadPanel
              contentId={record.id}
              pdfPath={pdfPath}
              onChange={next => {
                setPdfPath(next)
                form.setFieldValue('pdf_path', next)
              }}
            />
            <Form.Item name="pdf_path" hidden>
              <input type="hidden" />
            </Form.Item>
          </Card>

          <div style={{ marginBottom: 16 }}>
            <ContentAnalyticsPanel contentId={record.id} />
          </div>
        </>
      ) : (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={translate('admin.screens.contentForm.saveFirstTitle')}
          description={translate('admin.screens.contentForm.saveFirstBody')}
        />
      )}

      <Card size="small" title={translate('admin.screens.contentForm.publicationSection')}>
        <Form.Item
          name="is_free"
          label={translate('admin.columns.freePreview')}
          valuePropName="checked"
          help={translate('admin.screens.contentForm.freePreviewHelp')}
        >
          <Switch />
        </Form.Item>

        {publishBlocked ? (
          <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={publishBlocked} />
        ) : null}

        <Form.Item
          name="is_published"
          label={translate('admin.screens.contentForm.published')}
          valuePropName="checked"
        >
          <Switch onChange={onPublishToggle} />
        </Form.Item>
      </Card>
    </>
  )
}

function usePublishGuard(form: FormInstance<ContentItem>, record?: ContentItem) {
  const translate = useAdminTranslate()
  const [publishBlocked, setPublishBlocked] = useState<string | null>(null)

  const onPublishToggle = (next: boolean) => {
    if (!next) {
      setPublishBlocked(null)
      return
    }
    // AC3 — no ready video, no publication.
    if (record?.video_status !== 'ready') {
      form.setFieldValue('is_published', false)
      setPublishBlocked(translate('admin.screens.contentForm.publishBlockedVideo'))
      return
    }
    const result = checkBilingualForPublish(form.getFieldsValue(true), [
      { field: 'title', label: translate('admin.screens.contentForm.titleRuleLabel') },
    ])
    if (!result.ok) {
      form.setFieldValue('is_published', false)
      setPublishBlocked(result.message ?? null)
      return
    }
    setPublishBlocked(null)
  }

  return { publishBlocked, onPublishToggle }
}

export const ContentItemCreatePage = () => {
  const translate = useAdminTranslate()
  const { formProps, saveButtonProps, form } = useForm<ContentItem, HttpError, ContentItem>({
    resource: 'content_items',
    action: 'create',
    redirect: 'edit',
  })
  const { publishBlocked, onPublishToggle } = usePublishGuard(form)

  return (
    <Create saveButtonProps={saveButtonProps} title={translate('admin.pages.newContent')}>
      <Form {...formProps} layout="vertical">
        <Typography.Paragraph type="secondary">
          {translate('admin.screens.contentForm.createHint')}
        </Typography.Paragraph>
        <Fields form={form} publishBlocked={publishBlocked} onPublishToggle={onPublishToggle} />
      </Form>
    </Create>
  )
}

export const ContentItemEditPage = () => {
  const translate = useAdminTranslate()
  const { formProps, saveButtonProps, form, query, onFinish } = useForm<
    ContentItem,
    HttpError,
    ContentItem
  >({
    resource: 'content_items',
    action: 'edit',
  })
  const record = query?.data?.data
  const { publishBlocked, onPublishToggle } = usePublishGuard(form, record)

  const handleFinish = async (values: ContentItem) => {
    await onFinish(values)
    if (record && record.is_published !== values.is_published) {
      await recordAudit({
        action: values.is_published ? 'content.publish' : 'content.unpublish',
        target: 'content_items',
        targetId: record.id,
        before: { is_published: record.is_published },
        after: { is_published: values.is_published },
      })
    }
  }

  return (
    <Edit saveButtonProps={saveButtonProps} title={translate('admin.pages.editContent')}>
      <Form {...formProps} layout="vertical" onFinish={handleFinish}>
        <Fields
          form={form}
          record={record}
          publishBlocked={publishBlocked}
          onPublishToggle={onPublishToggle}
        />
      </Form>
    </Edit>
  )
}
